/*
 * NodeConfigPanel — right sidebar showing config form for the selected node.
 * Renders different form fields based on node type.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { captureAPI } from '../../services/api';
import { CATEGORY_ORDER } from '../../constants/fieldCategories';
import { NON_CLOUD_CHANNEL_TYPES, presentHealth } from '../../constants/cloudChannels';
import NewIndexFieldModal from './NewIndexFieldModal';
import { activeDestinations, destinationListsFrom, destinationProblem, destinationStatus } from './destinationStatus';
import {
  buildPickerRows,
  buildTargetOptions,
  duplicateTargets,
  isRowSent,
  prepareConfigForSave,
  selectionMode,
  setRename,
  toggleSend,
} from './publishFieldSelection';
import './NodeConfigPanel.css';

const CONDITION_OPERATORS = [
  { value: 'equals', label: '=' },
  { value: 'not_equals', label: '≠' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'not contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'greater_than', label: '>' },
  { value: 'less_than', label: '<' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
  { value: 'matches_regex', label: 'regex' },
];

const NodeConfigPanel = ({ node, workflowId, onUpdate, onDelete, onClose }) => {
  const [label, setLabel] = useState('');
  const [config, setConfig] = useState({});
  // Every destination a Publish step could be holding the id of: active,
  // inactive and (named only) deleted. null until loaded, and if loading fails.
  const [destinationLists, setDestinationLists] = useState(null);
  const destinations = useMemo(() => activeDestinations(destinationLists), [destinationLists]);
  // Publish node rows (set by PublishConfig once loaded) — used on Update Node
  // to convert a legacy included_fields allowlist.
  const publishRowsRef = useRef(null);

  // Reset state when selected node changes
  useEffect(() => {
    if (!node) return;
    setLabel(node.data?.label || '');
    setConfig(node.data?.config ? { ...node.data.config } : {});
    publishRowsRef.current = null;
  }, [node]);

  // Load destinations for publish node
  useEffect(() => {
    if (node?.type !== 'output_publish') return;
    captureAPI.destinations.list({ includeInactive: true, includeDeleted: true })
      .then(res => setDestinationLists(destinationListsFrom(res)))
      .catch(() => setDestinationLists(null));
  }, [node?.type]);

  const setConfigField = useCallback((key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleApply = () => {
    const finalConfig = node.type === 'output_publish'
      ? prepareConfigForSave(config, publishRowsRef.current)
      : config;
    if (finalConfig !== config) setConfig(finalConfig);
    onUpdate(node.id, { label, config: finalConfig });
  };

  if (!node) return null;

  const nodeType = node.type;
  const nodeData = node.data || {};

  return (
    <div className="ncp-panel">
      <div className="ncp-header">
        <span className="ncp-header-icon">{nodeData.icon || '⚡'}</span>
        <div className="ncp-header-info">
          <div className="ncp-header-label">{nodeData.label || nodeType}</div>
          <div className="ncp-header-type">{nodeType}</div>
        </div>
        <button className="ncp-close" onClick={onClose} title="Close">×</button>
      </div>

      <div className="ncp-body">
        {nodeData.description && (
          <div className="ncp-description">{nodeData.description}</div>
        )}

        {/* Node label (shared for all types) */}
        <div className="ncp-label-field">
          <div className="ncp-field">
            <label>Display Label</label>
            <input
              className="ncp-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Node label"
            />
          </div>
        </div>

        {/* Per-type config forms */}
        {nodeType === 'trigger_upload' && (
          <BatchSplitConfig config={config} onChange={setConfigField} />
        )}

        {/* BatchSplitConfig rides along exactly as it does for the other two
            triggers. Cloud source is registered in getTriggerNodeSplitMode, so
            omitting it here would leave split mode unreachable for cloud
            documents with nothing to show that it was missing. */}
        {nodeType === 'trigger_cloud_source' && (
          <>
            <CloudSourceConfig
              config={config}
              onChange={setConfigField}
              workflowId={workflowId}
            />
            <BatchSplitConfig config={config} onChange={setConfigField} />
          </>
        )}

        {(nodeType === 'action_await_ocr' || nodeType === 'action_ocr') && (
          <OcrConfig config={config} onChange={setConfigField} />
        )}

        {nodeType === 'action_split' && (
          <SplitConfig config={config} onChange={setConfigField} />
        )}

        {nodeType === 'action_extract' && (
          <ExtractConfig config={config} onChange={setConfigField} />
        )}

        {nodeType === 'action_classify' && (
          <ClassifyConfig config={config} onChange={setConfigField} />
        )}

        {nodeType === 'decision' && (
          <DecisionConfig config={config} setConfig={setConfig} />
        )}

        {nodeType === 'action_human_review' && (
          <HumanReviewConfig config={config} onChange={setConfigField} />
        )}

        {nodeType === 'action_set_field' && (
          <SetFieldConfig config={config} onChange={setConfigField} />
        )}

        {nodeType === 'action_field_mapping' && (
          <JsonConfig config={config} setConfig={setConfig} label="Field Mapping (JSON)" />
        )}

        {nodeType === 'output_publish' && (
          <PublishConfig
            config={config}
            onChange={setConfigField}
            setConfig={setConfig}
            destinations={destinations}
            destinationLists={destinationLists}
            workflowId={workflowId}
            rowsRef={publishRowsRef}
          />
        )}

        {nodeType === 'output_archive' && (
          <ArchiveConfig config={config} onChange={setConfigField} />
        )}
      </div>

      <div className="ncp-footer">
        <button className="ncp-update-btn" onClick={handleApply}>
          Update Node
        </button>
        <button className="ncp-delete-btn" onClick={() => onDelete(node.id)}>
          Delete Node
        </button>
      </div>
    </div>
  );
};

/* ── Per-type config sub-components ── */

/**
 * Cloud Source trigger config — pick the connected channel this workflow reads.
 *
 * The binding is `config.channel_id` and nothing else; `channel_name` is written
 * beside it purely so the canvas node can render a name without a lookup (see
 * BaseNode.getConfigSummary).
 *
 * SERVER ENFORCES THE ONE-CHANNEL-ONE-WORKFLOW RULE on save. Greying a channel
 * already used by another workflow is clarity, not security — a hand-crafted
 * save still gets a 400 naming the competing workflow.
 */
function CloudSourceConfig({ config, onChange, workflowId }) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    captureAPI.ingestion
      .listChannels()
      .then((res) => {
        if (!active) return;
        const all = Array.isArray(res?.channels) ? res.channels : [];
        // Filtered by TYPE, from the shared list. manual_upload is a real
        // channel the poller never enumerates (and a retired folder_watch row
        // may still be listed by an older server); offering one here would let
        // an admin build a workflow that can never receive a document.
        setChannels(all.filter((c) => !NON_CLOUD_CHANNEL_TYPES.includes(c.channel_type)));
      })
      .catch((err) => {
        if (active) setError(err?.response?.data?.error || 'Could not load cloud sources.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const selected = channels.find((c) => c.id === config.channel_id) || null;
  const health = selected ? presentHealth(selected.health_status) : null;

  const handlePick = (channelId) => {
    onChange('channel_id', channelId || undefined);
    const picked = channels.find((c) => c.id === channelId);
    onChange('channel_name', picked ? picked.display_name : undefined);
  };

  if (loading) {
    return <div className="ncp-field-hint">Loading cloud sources…</div>;
  }

  if (error) {
    return <div className="ncp-field-hint">{error}</div>;
  }

  if (channels.length === 0) {
    return (
      <div className="ncp-field">
        <div className="ncp-field-hint">
          No cloud sources connected yet.{' '}
          <Link to="/settings/ingestion">Connect one in Document Sources</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="ncp-field">
      <label>Cloud source</label>
      <select
        className="ncp-select"
        value={config.channel_id || ''}
        onChange={(e) => handlePick(e.target.value)}
      >
        <option value="">Choose a connected source…</option>
        {channels.map((channel) => {
          // `used` is true only for ANOTHER workflow. Without workflowId every
          // already-bound channel would grey out, including this workflow's own
          // — which would make a saved cloud workflow impossible to re-open and
          // edit.
          const used = channel.workflow_id && channel.workflow_id !== workflowId;
          const folder = channel.watch_folder_path || 'no folder selected';
          return (
            <option key={channel.id} value={channel.id} disabled={Boolean(used)}>
              {`${channel.display_name} — ${folder}`}
              {used ? ` (used by ${channel.workflow_name})` : ''}
            </option>
          );
        })}
      </select>

      {/* Same wording the Document Sources page uses, from the same module. A
          workflow bound to a source that is not currently reachable is worth
          saying here rather than making the admin go and look. */}
      {selected && selected.health_status !== 'connected' && (
        <div className="ncp-field-hint">{health.label}</div>
      )}
    </div>
  );
}

function BatchSplitConfig({ config, onChange }) {
  const mode = config.batch_split_mode || 'none';

  return (
    <div className="ncp-section">
      <div className="ncp-section-label">Batch Document Splitting</div>
      <div className="ncp-field">
        <label>Split Mode</label>
        <select
          className="ncp-select"
          value={mode}
          onChange={(e) => onChange('batch_split_mode', e.target.value)}
        >
          <option value="none">None — single documents only</option>
          <option value="auto">Automatic — split without review</option>
          <option value="manual">Manual Confirmation — review before processing</option>
        </select>
      </div>
      {mode === 'auto' && (
        <div className="ncp-field-hint">
          Documents will be split automatically. Low-confidence boundaries are flagged in the batch queue.
        </div>
      )}
      {mode === 'manual' && (
        <div className="ncp-field-hint">
          After upload, proposed split points will appear in the batch queue for your review before processing begins.
        </div>
      )}
    </div>
  );
}

function OcrConfig({ config, onChange }) {
  return (
    <>
      <div className="ncp-field">
        <label>OCR Engine</label>
        <select
          className="ncp-select"
          value={config.engine || 'auto'}
          onChange={(e) => onChange('engine', e.target.value)}
        >
          <option value="auto">Auto (recommended)</option>
          <option value="paddle">PaddleOCR</option>
          <option value="tesseract">Tesseract</option>
        </select>
      </div>
      <div className="ncp-field">
        <label className="ncp-checkbox-row">
          <input
            type="checkbox"
            checked={config.fallback !== false}
            onChange={(e) => onChange('fallback', e.target.checked)}
          />
          Enable AI fallback if OCR fails
        </label>
      </div>
    </>
  );
}

function SplitConfig({ config, onChange }) {
  return (
    <>
      <div className="ncp-field">
        <label>Split Method</label>
        <select
          className="ncp-select"
          value={config.split_method || 'auto'}
          onChange={(e) => onChange('split_method', e.target.value)}
        >
          <option value="auto">Auto — detect boundaries using OCR text</option>
          <option value="manual">Manual — propose splits for user review</option>
        </select>
      </div>
      <div className="ncp-field">
        <label>Minimum Pages to Attempt Split</label>
        <input
          className="ncp-input"
          type="number"
          min="2"
          max="100"
          value={config.min_pages || 2}
          onChange={(e) => onChange('min_pages', parseInt(e.target.value))}
        />
        <p className="ncp-hint">Single-page documents are never split. Default: 2</p>
      </div>
      <div className="ncp-info">
        <p>📄 Detects document type changes between pages using your configured Document Types and their detection keywords.</p>
        <p>✂️ Each detected document is split into a separate record and processed independently through the rest of the workflow.</p>
      </div>
    </>
  );
}

function ExtractConfig({ config, onChange }) {
  return (
    <>
      <div className="ncp-field">
        <label>Extraction Mode</label>
        <select
          className="ncp-select"
          value={config.mode || 'template'}
          onChange={(e) => onChange('mode', e.target.value)}
        >
          <option value="template">Template-based</option>
          <option value="ai_assisted">AI-Assisted</option>
          <option value="ai_primary">AI-Primary</option>
        </select>
      </div>
      {config.mode === 'template' && (
        <div className="ncp-field">
          <label>Template ID</label>
          <input
            className="ncp-input"
            value={config.template_id || ''}
            onChange={(e) => onChange('template_id', e.target.value)}
            placeholder="UUID of template"
          />
        </div>
      )}
    </>
  );
}

function ClassifyConfig({ config, onChange }) {
  return (
    <>
      <div className="ncp-field">
        <label>Classification Method</label>
        <select
          className="ncp-select"
          value={config.method || 'template_match'}
          onChange={(e) => onChange('method', e.target.value)}
        >
          <option value="template_match">Template Match</option>
          <option value="ai">AI Classification</option>
        </select>
      </div>
      <div className="ncp-field">
        <label>Confidence Threshold</label>
        <input
          className="ncp-input"
          type="number"
          step={0.05}
          min={0}
          max={1}
          value={config.confidence_threshold ?? 0.8}
          onChange={(e) => onChange('confidence_threshold', Number(e.target.value))}
        />
      </div>
    </>
  );
}

/**
 * FieldKeyCombobox — searchable dropdown with grouped system + extraction fields.
 * Supports typeahead filtering, custom values, and shows field descriptions.
 */
function FieldKeyCombobox({ value, onChange, fieldData }) {
  const [search, setSearch] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false });

  // Sync external value changes into the search input
  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  // Close dropdown when clicking outside both the input wrapper and the portal'd panel
  useEffect(() => {
    const handleClickOutside = (e) => {
      const target = e.target;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        !target.closest('.fkc-dropdown')
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Compute portal panel position relative to the input. Flip upward if no room below.
  const computePosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const estimatedHeight = 240; // matches .fkc-dropdown max-height
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
    setDropdownPosition({
      top: openUpward
        ? rect.top + window.scrollY
        : rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      openUpward,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    computePosition();
    const handler = () => computePosition();
    window.addEventListener('scroll', handler, { passive: true, capture: true });
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, { capture: true });
      window.removeEventListener('resize', handler);
    };
  }, [isOpen, computePosition]);

  const filterText = search.toLowerCase();

  const filteredSystem = (fieldData.systemFields || []).filter(
    f => f.key.toLowerCase().includes(filterText) ||
         f.label.toLowerCase().includes(filterText) ||
         (f.description || '').toLowerCase().includes(filterText)
  );

  const filteredExtraction = (fieldData.extractionFields || []).filter(
    f => f.key.toLowerCase().includes(filterText) ||
         f.label.toLowerCase().includes(filterText)
  );

  const hasResults = filteredSystem.length > 0 || filteredExtraction.length > 0;

  const selectField = (key) => {
    setSearch(key);
    onChange(key);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    onChange(val);
    if (!isOpen) setIsOpen(true);
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Find display label for the current value
  const allFields = [...(fieldData.systemFields || []), ...(fieldData.extractionFields || [])];
  const matchedField = allFields.find(f => f.key === value);

  return (
    <div className="fkc-wrapper" ref={wrapperRef}>
      <input
        ref={inputRef}
        className="ncp-input fkc-input"
        value={search}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder="Search or type field key…"
        autoComplete="off"
      />
      {matchedField && search === matchedField.key && (
        <div className="fkc-field-hint">{matchedField.label}</div>
      )}
      {isOpen && createPortal(
        <div
          className="fkc-dropdown"
          style={{
            position: 'absolute',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            ...(dropdownPosition.openUpward ? { transform: 'translateY(-100%)' } : {}),
          }}
        >
          {!hasResults && (
            <div className="fkc-empty">
              {search ? `No matching fields — "${search}" will be used as-is` : 'Type to search fields'}
            </div>
          )}
          {filteredSystem.length > 0 && (
            <>
              <div className="fkc-group-label">System Fields</div>
              {filteredSystem.map(f => (
                <div
                  key={f.key}
                  className={`fkc-option ${value === f.key ? 'selected' : ''}`}
                  onClick={() => selectField(f.key)}
                  title={f.description}
                >
                  <span className="fkc-option-key">{f.key}</span>
                  <span className="fkc-option-label">{f.label}</span>
                </div>
              ))}
            </>
          )}
          {filteredExtraction.length > 0 && (
            <>
              <div className="fkc-group-label">Extraction Fields</div>
              {filteredExtraction.map(f => (
                <div
                  key={f.key}
                  className={`fkc-option ${value === f.key ? 'selected' : ''}`}
                  onClick={() => selectField(f.key)}
                >
                  <span className="fkc-option-key">{f.key}</span>
                  <span className="fkc-option-label">{f.label}</span>
                </div>
              ))}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

function DecisionConfig({ config, setConfig }) {
  const conditions = config.conditions || [];
  const logic = config.logic || 'AND';
  const [fieldData, setFieldData] = useState({ systemFields: [], extractionFields: [] });

  // Load available field keys on mount
  useEffect(() => {
    captureAPI.workflows.getFieldKeys()
      .then(res => {
        if (res.ok) {
          setFieldData({
            systemFields: res.systemFields || [],
            extractionFields: res.extractionFields || [],
          });
        }
      })
      .catch(err => console.warn('[DecisionConfig] Failed to load field keys:', err.message));
  }, []);

  const addCondition = () => {
    setConfig(prev => ({
      ...prev,
      conditions: [...(prev.conditions || []), { field: '', operator: 'equals', value: '' }],
    }));
  };

  const removeCondition = (idx) => {
    setConfig(prev => ({
      ...prev,
      conditions: (prev.conditions || []).filter((_, i) => i !== idx),
    }));
  };

  const updateCondition = (idx, key, value) => {
    setConfig(prev => {
      const next = [...(prev.conditions || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, conditions: next };
    });
  };

  const toggleLogic = (val) => {
    setConfig(prev => ({ ...prev, logic: val }));
  };

  return (
    <>
      {conditions.length > 1 && (
        <div className="ncp-logic-toggle">
          <button
            className={`ncp-logic-btn ${logic === 'AND' ? 'active' : ''}`}
            onClick={() => toggleLogic('AND')}
          >
            AND
          </button>
          <button
            className={`ncp-logic-btn ${logic === 'OR' ? 'active' : ''}`}
            onClick={() => toggleLogic('OR')}
          >
            OR
          </button>
        </div>
      )}

      {conditions.map((cond, idx) => (
        <div className="ncp-condition" key={idx}>
          <div className="ncp-condition-row">
            <FieldKeyCombobox
              value={cond.field || ''}
              onChange={(val) => updateCondition(idx, 'field', val)}
              fieldData={fieldData}
            />
            <button
              className="ncp-condition-remove"
              onClick={() => removeCondition(idx)}
              title="Remove condition"
            >
              ✕
            </button>
          </div>
          <div className="ncp-condition-row">
            <select
              className="ncp-select"
              value={cond.operator || 'equals'}
              onChange={(e) => updateCondition(idx, 'operator', e.target.value)}
            >
              {CONDITION_OPERATORS.map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
            <input
              className="ncp-input"
              value={cond.value || ''}
              onChange={(e) => updateCondition(idx, 'value', e.target.value)}
              placeholder="Value"
            />
          </div>
        </div>
      ))}

      <button className="ncp-add-condition" onClick={addCondition}>
        + Add Condition
      </button>
    </>
  );
}

function HumanReviewConfig({ config, onChange }) {
  return (
    <>
      <div className="ncp-field">
        <label>Review Queue</label>
        <input
          className="ncp-input"
          value={config.review_queue || ''}
          onChange={(e) => onChange('review_queue', e.target.value)}
          placeholder="default"
        />
      </div>
      <div className="ncp-field">
        <label>Instructions</label>
        <textarea
          className="ncp-input"
          value={config.instructions || ''}
          onChange={(e) => onChange('instructions', e.target.value)}
          placeholder="Instructions for the reviewer"
          rows={3}
          style={{ minHeight: 60, fontFamily: 'inherit' }}
        />
      </div>
      <div className="ncp-field">
        <label>Timeout (hours)</label>
        <input
          className="ncp-input"
          type="number"
          min={0}
          value={config.timeout_hours || ''}
          onChange={(e) => onChange('timeout_hours', e.target.value ? Number(e.target.value) : null)}
          placeholder="No timeout"
        />
      </div>
    </>
  );
}

function SetFieldConfig({ config, onChange }) {
  return (
    <>
      <div className="ncp-field">
        <label>Field Key</label>
        <input
          className="ncp-input"
          value={config.field_key || ''}
          onChange={(e) => onChange('field_key', e.target.value)}
          placeholder="e.g. vendor_name"
        />
      </div>
      <div className="ncp-field">
        <label>Value Template</label>
        <input
          className="ncp-input"
          value={config.value_template || ''}
          onChange={(e) => onChange('value_template', e.target.value)}
          placeholder="Static value or {field} tokens"
        />
      </div>
    </>
  );
}

const NEW_FIELD_VALUE = '__new_index_field__';

/**
 * Publish node config. For a document_library destination the node owns the
 * per-workflow field table: Send (excluded_fields denylist) and Rename
 * (field_mapping, renames only). Rows = active catalog fields (scope
 * both/capture) UNION the tenant's distinct extracted keys; rename targets =
 * Library-visible catalog fields (active, scope both/library), plus
 * "+ New index field…". Selection logic lives in ./publishFieldSelection.
 *
 * `rowsRef` hands the loaded rows to the panel's Update Node, which converts a
 * legacy included_fields allowlist on save (prepareConfigForSave). It stays
 * null until the rows have loaded, so a failed load never converts.
 *
 * File/folder destinations keep their mapping on the destination.
 */
function PublishConfig({ config, onChange, setConfig, destinations, destinationLists, workflowId, rowsRef }) {
  const selectedDest = destinations.find(d => d.id === config.destination_id);
  // The stored destination is not one that can be chosen: deleted, gone, or
  // switched off. The step says so, and the list keeps showing what it holds
  // rather than falling back to "Select destination" like a step never set up.
  const stored = destinationStatus(config.destination_id, destinationLists);
  const storedProblem = destinationProblem(stored);
  const storedLabel =
    stored.state === 'deleted' ? `${stored.name || 'Destination'} (deleted)`
    : stored.state === 'inactive' ? `${stored.name || 'Destination'} (inactive)`
    : 'Destination no longer exists';
  const isLibrary = selectedDest?.connector_type === 'document_library';

  const [catalog, setCatalog] = useState([]);
  const [extractedKeys, setExtractedKeys] = useState([]);
  const [fieldsLoaded, setFieldsLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [modalRowKey, setModalRowKey] = useState(null);
  const [notice, setNotice] = useState(null);

  // Unscoped: one read gives both the picker scope (both/capture) and the
  // rename-target scope (both/library).
  const loadCatalog = useCallback(
    () => captureAPI.getFieldLibrary().then(res => setCatalog(res?.fields || [])),
    [],
  );

  useEffect(() => {
    if (!isLibrary) return undefined;
    let cancelled = false;
    setFieldsLoaded(false);
    setLoadFailed(false);
    Promise.allSettled([captureAPI.getFieldLibrary(), captureAPI.destinations.getFieldKeys()])
      .then(([lib, keys]) => {
        if (cancelled) return;
        const libOk = lib.status === 'fulfilled';
        const keysOk = keys.status === 'fulfilled' && keys.value?.ok !== false;
        setCatalog(libOk ? (lib.value?.fields || []) : []);
        setExtractedKeys(keysOk ? (keys.value?.fieldKeys || []) : []);
        setLoadFailed(!libOk || !keysOk);
        setFieldsLoaded(true);
      });
    return () => { cancelled = true; };
  }, [isLibrary]);

  const rows = useMemo(() => {
    const built = buildPickerRows(catalog, extractedKeys);
    const order = (c) => {
      const i = CATEGORY_ORDER.indexOf(c);
      return i === -1 ? CATEGORY_ORDER.length : i;
    };
    return built.sort((a, b) => order(a.category) - order(b.category) || a.label.localeCompare(b.label));
  }, [catalog, extractedKeys]);
  const targets = useMemo(() => buildTargetOptions(catalog), [catalog]);

  useEffect(() => {
    if (!rowsRef) return;
    rowsRef.current = isLibrary && fieldsLoaded && !loadFailed && rows.length > 0 ? rows : null;
  }, [rowsRef, isLibrary, fieldsLoaded, loadFailed, rows]);

  const mapping = config.field_mapping || {};
  const targetKeys = new Set(targets.map(t => t.field_key));
  const sentCount = rows.filter(r => isRowSent(config, r)).length;
  const dupTargets = duplicateTargets(mapping);
  const mode = selectionMode(config);

  const handleRename = (fieldKey, value) => {
    if (value === NEW_FIELD_VALUE) {
      setNotice(null);
      setModalRowKey(fieldKey);
      return;
    }
    setConfig(prev => setRename(prev, fieldKey, value));
  };

  const handleCreated = (field, failedDocTypes) => {
    const rowKey = modalRowKey;
    setModalRowKey(null);
    // Select it in that row now; the re-fetch then brings it into every list.
    setConfig(prev => setRename(prev, rowKey, field.field_key));
    setNotice(failedDocTypes?.length
      ? `Created ${field.field_key}, but it could not be linked to: ${failedDocTypes.join(', ')}.`
      : null);
    loadCatalog().catch(() => {});
  };

  return (
    <>
      <div className="ncp-field">
        <label>Destination</label>
        <select
          className="ncp-select"
          value={config.destination_id || ''}
          onChange={(e) => onChange('destination_id', e.target.value)}
        >
          <option value="">— Select destination —</option>
          {storedProblem && (
            <option value={config.destination_id} disabled>{storedLabel}</option>
          )}
          {destinations.map(d => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        {storedProblem && (
          <div className="ncp-error ncp-destination-problem" role="alert" data-state={stored.state}>
            {storedProblem}
          </div>
        )}
        {selectedDest && !isLibrary && (
          <div className="ncp-hint">Field renaming for a file or folder export is set on the destination.</div>
        )}
      </div>
      {isLibrary && (
        <div className="ncp-field">
          <label>Fields sent to Library</label>
          {!fieldsLoaded ? (
            <div className="ncp-hint">Loading fields…</div>
          ) : rows.length === 0 ? (
            <div className="ncp-hint">{loadFailed ? 'Could not load fields.' : 'No capture fields defined.'}</div>
          ) : (
            <>
              <div className="ncp-hint" data-testid="ncp-sent-summary">
                Sending {sentCount} of {rows.length} fields
              </div>
              {loadFailed && (
                <div className="ncp-error">Some fields could not be loaded; the list may be incomplete.</div>
              )}
              {mode === 'legacy_included' && (
                <div className="ncp-hint">
                  Saved with an older field list. Update Node converts it: every field not ticked here stops being sent,
                  and fields added later are sent.
                </div>
              )}
              <div className="ncp-map-table-wrap">
                <table className="ncp-map-table">
                  <thead>
                    <tr>
                      <th>Send</th>
                      <th>Field</th>
                      <th>Rename to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => {
                      const sent = isRowSent(config, r);
                      const current = mapping[r.field_key] || '';
                      return (
                        <tr key={r.field_key} data-field-key={r.field_key} className={sent ? '' : 'ncp-map-row-off'}>
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`Send ${r.field_key}`}
                              checked={sent}
                              onChange={() => setConfig(prev => toggleSend(prev, rows, r.field_key))}
                            />
                          </td>
                          <td title={r.field_key}>
                            {r.label}
                            {!r.inCatalog && <span className="ncp-map-tag">extracted</span>}
                          </td>
                          <td>
                            <select
                              className="ncp-select"
                              aria-label={`Rename ${r.field_key}`}
                              value={current}
                              onChange={(e) => handleRename(r.field_key, e.target.value)}
                            >
                              <option value={NEW_FIELD_VALUE}>+ New index field…</option>
                              <option value="">(same name)</option>
                              {current && !targetKeys.has(current) && (
                                <option value={current}>{current}</option>
                              )}
                              {targets.filter(t => t.field_key !== r.field_key).map(t => (
                                <option key={t.field_key} value={t.field_key}>
                                  {t.label === t.field_key ? t.field_key : `${t.label} (${t.field_key})`}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {dupTargets.length > 0 && (
                <div className="ncp-error" role="alert">
                  More than one field is renamed to {dupTargets.join(', ')}. Each target can be used once; the save will be refused.
                </div>
              )}
              {notice && <div className="ncp-error" role="status">{notice}</div>}
            </>
          )}
        </div>
      )}
      <NewIndexFieldModal
        open={modalRowKey !== null}
        workflowId={workflowId}
        onClose={() => setModalRowKey(null)}
        onCreated={handleCreated}
      />
      <div className="ncp-field">
        <label className="ncp-checkbox-row">
          <input
            type="checkbox"
            checked={config.auto_publish === true}
            onChange={(e) => onChange('auto_publish', e.target.checked)}
          />
          Auto-publish (skip confirmation)
        </label>
      </div>
      <div className="ncp-field">
        <label>Folder Rule</label>
        <input
          className="ncp-input"
          value={config.folder_rule || ''}
          onChange={(e) => onChange('folder_rule', e.target.value)}
          placeholder="{vendor_name}/{invoice_date}"
        />
      </div>
      <div className="ncp-field">
        <label>Filename Rule</label>
        <input
          className="ncp-input"
          value={config.filename_rule || ''}
          onChange={(e) => onChange('filename_rule', e.target.value)}
          placeholder="{vendor_name}_{invoice_number}"
        />
      </div>
    </>
  );
}

function ArchiveConfig({ config, onChange }) {
  return (
    <div className="ncp-field">
      <label>Retention (days)</label>
      <input
        className="ncp-input"
        type="number"
        min={0}
        value={config.retention_days || ''}
        onChange={(e) => onChange('retention_days', e.target.value ? Number(e.target.value) : null)}
        placeholder="e.g. 365"
      />
    </div>
  );
}

function JsonConfig({ config, setConfig, label }) {
  const [text, setText] = useState('');

  useEffect(() => {
    setText(JSON.stringify(config, null, 2));
  }, [config]);

  const handleBlur = () => {
    try {
      const parsed = JSON.parse(text);
      setConfig(parsed);
    } catch {
      // invalid JSON — don't update
    }
  };

  return (
    <div className="ncp-field">
      <label>{label || 'Configuration (JSON)'}</label>
      <textarea
        className="ncp-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleBlur}
        rows={8}
      />
    </div>
  );
}

export default NodeConfigPanel;
